import { apiClient, ApiResponse } from './client'
import type {
  ExplainPhoto,
  ExplainPhotoTokenResponse,
} from './lcLearning'
import type {
  AdvanceLessonResponse,
  BuildupStepGradeBatchRequest,
  BuildupStepGradeRequest,
  ClassRosterItemView,
  HandheldGradingSummaryItemView,
  CoachingLessonDetailResponse,
  ComponentProgressDetailView,
  DesignatedReviewView,
  DesignateReviewRequest,
  EssentialCheckRatingBatchRequest,
  EssentialCheckRatingRequest,
  GrowthAlertPreviewResponse,
  GrowthAlertShareResponse,
  PublicGrowthAlertResponse,
  LcCoachingGuide,
  LcReviewCandidateView,
  LcTestDetailView,
  LcTestListView,
  LcTestPrintView,
  LcTestReportView,
  LearnerLibraryResponse,
  LearnerTodayResponse,
  LessonCompleteResponse,
  LessonCompletionReadinessResponse,
  OverrideQuizAnswerRequest,
  TodayClassView,
  TodayClassRosterSearchItemView,
  WorkbookGradeRequest,
  WorkbookGradeView,
  WorkbookMode,
} from '@/types/coaching'

export interface LessonCompletionScope { attemptScope: 'PROGRESS' | 'REVIEW'; learningAttemptId?: number | null }

export const lcCoachingApi = {
  getTodayClasses: async (): Promise<TodayClassView[]> => {
    const res = await apiClient.get<ApiResponse<TodayClassView[]>>(
      '/lc/admin/coaching/classes',
    )
    return res.data.data
  },
  getClassRoster: async (timeslotId: number): Promise<ClassRosterItemView[]> => {
    const res = await apiClient.get<ApiResponse<ClassRosterItemView[]>>(
      `/lc/admin/coaching/classes/${timeslotId}/roster`,
    )
    return res.data.data
  },
  /** timeslotId를 생략하면 오늘 전체 클래스의 요약을 반환한다. */
  getHandheldGradingSummary: async (timeslotId?: number | null): Promise<HandheldGradingSummaryItemView[]> => {
    const res = await apiClient.get<ApiResponse<HandheldGradingSummaryItemView[]>>(
      '/lc/admin/coaching/handheld/grading-summary',
      { params: timeslotId == null ? undefined : { timeslotId } },
    )
    return res.data.data
  },
  searchTodayClassRoster: async (query: string): Promise<TodayClassRosterSearchItemView[]> => {
    const res = await apiClient.get<ApiResponse<TodayClassRosterSearchItemView[]>>(
      '/lc/admin/coaching/classes/roster/search',
      { params: { q: query } },
    )
    return res.data.data
  },
  getLearnerToday: async (
    learnerId: number,
    date?: string,
  ): Promise<LearnerTodayResponse> => {
    const params = date ? { date } : undefined
    const res = await apiClient.get<ApiResponse<LearnerTodayResponse>>(
      `/lc/admin/coaching/learners/${learnerId}/today`,
      { params },
    )
    return res.data.data
  },
  getEssentialPrintQuizIds: async (learnerId: number, lessonId: number): Promise<string[]> => {
    const res = await apiClient.get<ApiResponse<{ quizIds: string[] }>>(
      `/lc/admin/coaching/learners/${learnerId}/lessons/${lessonId}/essential-print-quizzes`,
    )
    return res.data.data.quizIds
  },
  getLessonPrintQuizIds: async (learnerId: number, lessonId: number): Promise<string[]> => {
    const res = await apiClient.get<ApiResponse<{ quizIds: string[] }>>(
      `/lc/admin/coaching/learners/${learnerId}/lessons/${lessonId}/print-quizzes`,
    )
    return res.data.data.quizIds
  },
  // 콘텐츠 트리(과정>코스>모듈(=챕터)>레슨). 트리에는 Snowflake quizId 가 없어 일반 .get 안전.
  getLibrary: async (learnerId: number, range = 'all'): Promise<LearnerLibraryResponse> => {
    const res = await apiClient.get<ApiResponse<LearnerLibraryResponse>>(
      `/lc/admin/coaching/learners/${learnerId}/library`,
      { params: { range } },
    )
    return res.data.data
  },
  // 레슨 학습기록(최초 학습 + 복습 회차). initialAttempt.components 에 Big-Integer quizId 가 있으므로
  // CRITICAL ①: text 로 받아 정규식 변환(일반 JSON.parse 금지).
  getLessonDetail: async (
    learnerId: number,
    lessonId: number,
    courseId: number,
    moduleId: number,
  ): Promise<CoachingLessonDetailResponse> => {
    const res = await apiClient.get<string>(
      `/lc/admin/coaching/learners/${learnerId}/lessons/${lessonId}/detail`,
      {
        responseType: 'text',
        params: { courseId, moduleId },
      },
    )
    const normalized = (res.data as unknown as string).replace(
      /"quizId"\s*:\s*(\d+)/g,
      '"quizId":"$1"',
    )
    const parsed = JSON.parse(normalized) as ApiResponse<CoachingLessonDetailResponse>
    if (!parsed.data) throw new Error('Invalid lesson-detail response')
    return parsed.data
  },
  advanceLesson: async (learnerId: number, force = false): Promise<AdvanceLessonResponse> => {
    const res = await apiClient.post<ApiResponse<AdvanceLessonResponse>>(
      `/lc/admin/coaching/learners/${learnerId}/advance-lesson`,
      { force },
    )
    return res.data.data
  },
  // [레슨 완료] 교사 명시 완료(Phase 4d). force=true면 미완료 컴포넌트가 있어도 강제 완료.
  // buildup 채점과 무관 — Snowflake quizId 없어 일반 JSON.
  completeLesson: async (
    learnerId: number,
    lessonId: number,
    force = false,
    workbookMode: WorkbookMode = 'IMMEDIATE',
    scope?: LessonCompletionScope,
  ): Promise<LessonCompleteResponse> => {
    const res = await apiClient.post<ApiResponse<LessonCompleteResponse>>(
      `/lc/admin/coaching/learners/${learnerId}/lessons/${lessonId}/complete`,
      { force, workbookMode, ...scope },
    )
    return res.data.data
  },
  getLessonCompletionReadiness: async (
    learnerId: number,
    lessonId: number,
    workbookMode: WorkbookMode = 'IMMEDIATE',
    scope?: LessonCompletionScope,
  ): Promise<LessonCompletionReadinessResponse> => {
    const res = await apiClient.get<ApiResponse<LessonCompletionReadinessResponse>>(
      `/lc/admin/coaching/learners/${learnerId}/lessons/${lessonId}/completion-readiness`,
      { params: { workbookMode, ...scope } },
    )
    return res.data.data
  },
  designateReview: async (learnerId: number, request: DesignateReviewRequest): Promise<DesignatedReviewView> => {
    const res = await apiClient.post<ApiResponse<DesignatedReviewView>>(
      `/lc/admin/coaching/learners/${learnerId}/designated-reviews`,
      request,
    )
    return res.data.data
  },
  cancelDesignatedReview: async (
    learnerId: number,
    lessonId: number,
  ): Promise<{ reviewLessonId: number; cancelled: boolean }> => {
    const res = await apiClient.delete<ApiResponse<{ reviewLessonId: number; cancelled: boolean }>>(
      `/lc/admin/coaching/learners/${learnerId}/designated-reviews/${lessonId}`,
    )
    return res.data.data
  },
  listDesignatedReviews: async (learnerId: number): Promise<DesignatedReviewView[]> => {
    const res = await apiClient.get<ApiResponse<DesignatedReviewView[]>>(
      `/lc/admin/coaching/learners/${learnerId}/designated-reviews`,
    )
    return res.data.data
  },
  // 수업 종료 = 기존 교사 수동 하원 (CHECK_OUT). 학습자 앱이 IN→OUT 전이를 감지해 자동 로그아웃.
  endSession: async (learnerId: number, coachingNote?: string): Promise<{ updated: boolean }> => {
    const res = await apiClient.post<ApiResponse<{ updated: boolean }>>(
      '/lc/attendance/manual',
      { userId: learnerId, action: 'CHECK_OUT', coachingNote },
    )
    return res.data.data
  },
  updateAttendanceNote: async (
    learnerId: number,
    attendanceId: number,
    note: string | null,
    expectedVersion: number,
  ): Promise<{ attendanceId: number; attendanceDate: string; note: string | null; version: number }> => {
    const res = await apiClient.patch<ApiResponse<{
      attendanceId: number
      attendanceDate: string
      note: string | null
      version: number
    }>>(
      `/lc/admin/coaching/learners/${learnerId}/attendances/${attendanceId}/note`,
      { note, expectedVersion },
    )
    return res.data.data
  },
  // 수동 등원(당일 보정, 축1 B — spec 2026-06-12 §3.6)
  manualCheckIn: async (learnerId: number): Promise<{ updated: boolean }> => {
    const res = await apiClient.post<ApiResponse<{ updated: boolean }>>(
      '/lc/attendance/manual',
      { userId: learnerId, action: 'CHECK_IN' },
    )
    return res.data.data
  },
  // 결석 처리(+사유 — 기존 PRESENT는 복원 가능한 상태로 무효화하고 ABSENT로 대체)
  markAbsent: async (learnerId: number, absenceReason?: string): Promise<{ attendanceId: number }> => {
    const res = await apiClient.post<ApiResponse<{ attendanceId: number }>>(
      '/lc/attendance/manual',
      { userId: learnerId, action: 'ABSENT', absenceReason },
    )
    return res.data.data
  },
  // 당일 결석 취소 — ABSENT를 철회하고 무효화했던 PRESENT를 원래 상태로 복원
  cancelAbsence: async (learnerId: number): Promise<{ cancelledCount: number }> => {
    const res = await apiClient.post<ApiResponse<{ cancelledCount: number }>>(
      '/lc/attendance/manual',
      { userId: learnerId, action: 'CANCEL_ABSENCE' },
    )
    return res.data.data
  },
  // 교사 정오답 수정(C1) — write-then-read. 응답에 Big-Integer quizId가 있으므로 text로 받아 정규식 변환.
  overrideQuizAnswer: async (
    learnerId: number,
    body: OverrideQuizAnswerRequest,
  ): Promise<ComponentProgressDetailView> => {
    // CRITICAL ①: 응답에 Big-Integer quizId가 있으므로 text로 받아 정규식 변환 (res.data.data 언랩 금지)
    const res = await apiClient.post<string>(
      `/lc/admin/coaching/learners/${learnerId}/quiz-override`,
      { ...body, pauseTimeMs: body.pauseTimeMs, answers: body.answers, appliedVars: body.appliedVars },
      { responseType: 'text' },
    )
    const normalized = (res.data as unknown as string).replace(/"quizId"\s*:\s*(\d+)/g, '"quizId":"$1"')
    const parsed = JSON.parse(normalized) as ApiResponse<ComponentProgressDetailView>
    if (!parsed.data) throw new Error('Invalid quiz-override response')
    return parsed.data
  },
  // 빌드업 STEP 항목 OXΔ 채점(Phase 4a). 응답(buildup detail)엔 Snowflake quizId 가 없어 일반 JSON.
  gradeBuildupStep: async (
    learnerId: number,
    body: BuildupStepGradeRequest,
  ): Promise<ComponentProgressDetailView> => {
    const res = await apiClient.post<ApiResponse<ComponentProgressDetailView>>(
      `/lc/admin/coaching/learners/${learnerId}/buildup-step-grade`,
      body,
    )
    return res.data.data
  },
  gradeBuildupSteps: async (
    learnerId: number,
    body: BuildupStepGradeBatchRequest,
  ): Promise<ComponentProgressDetailView> => {
    const res = await apiClient.post<ApiResponse<ComponentProgressDetailView>>(
      `/lc/admin/coaching/learners/${learnerId}/buildup-step-grades`,
      body,
    )
    return res.data.data
  },
  issueBuildupExplainPhotoToken: async (
    learnerId: number,
    componentProgressId: number,
  ): Promise<ExplainPhotoTokenResponse> => {
    const res = await apiClient.post<ApiResponse<ExplainPhotoTokenResponse>>(
      `/lc/admin/coaching/learners/${learnerId}/component-progress/${componentProgressId}/explain-photo-token`,
    )
    return res.data.data
  },
  listBuildupExplainPhotos: async (
    learnerId: number,
    componentProgressId: number,
  ): Promise<ExplainPhoto[]> => {
    const res = await apiClient.get<ApiResponse<ExplainPhoto[]>>(
      `/lc/admin/coaching/learners/${learnerId}/component-progress/${componentProgressId}/explain-photos`,
    )
    return res.data.data
  },

  // G7 — 워크북(오프라인 인쇄물) 강사 수기채점. 디지털 quiz 와 격리된 전용 레인이라 Snowflake quizId 없음(일반 JSON).
  getWorkbookGrades: async (
    learnerId: number,
    lessonId: number,
  ): Promise<WorkbookGradeView[]> => {
    const res = await apiClient.get<ApiResponse<WorkbookGradeView[]>>(
      `/lc/admin/coaching/learners/${learnerId}/workbook-grade`,
      { params: { lessonId } },
    )
    return res.data.data
  },
  gradeWorkbook: async (learnerId: number, body: WorkbookGradeRequest): Promise<void> => {
    await apiClient.post<ApiResponse<void>>(
      `/lc/admin/coaching/learners/${learnerId}/workbook-grade`,
      body,
    )
  },

  // ── Phase 6a — 레슨 가이드(교과정보/생각열기/핵심체크 5점) ──
  // 응답엔 Snowflake quizId 가 없어(레슨=일반 PK) 일반 JSON 안전.
  getGuide: async (
    learnerId: number,
    lessonId: number,
    learningAttemptId: number | null = null,
  ): Promise<LcCoachingGuide> => {
    const res = await apiClient.get<ApiResponse<LcCoachingGuide>>(
      `/lc/admin/coaching/learners/${learnerId}/lessons/${lessonId}/guide`,
      { params: learningAttemptId == null ? undefined : { learningAttemptId } },
    )
    return res.data.data
  },
  rateEssentialCheck: async (
    learnerId: number,
    body: EssentialCheckRatingRequest,
  ): Promise<LcCoachingGuide> => {
    const res = await apiClient.post<ApiResponse<LcCoachingGuide>>(
      `/lc/admin/coaching/learners/${learnerId}/essential-check-rating`,
      body,
    )
    return res.data.data
  },
  rateEssentialChecks: async (
    learnerId: number,
    body: EssentialCheckRatingBatchRequest,
  ): Promise<LcCoachingGuide> => {
    const res = await apiClient.post<ApiResponse<LcCoachingGuide>>(
      `/lc/admin/coaching/learners/${learnerId}/essential-check-ratings`,
      body,
    )
    return res.data.data
  },

  // 성장알림 미리보기 — 응답엔 Snowflake quizId 가 없어(레슨=일반 PK) 일반 JSON 안전.
  getGrowthAlertPreview: async (
    learnerId: number,
    date?: string,
  ): Promise<GrowthAlertPreviewResponse> => {
    const res = await apiClient.get<ApiResponse<GrowthAlertPreviewResponse>>(
      `/lc/admin/coaching/learners/${learnerId}/growth-alert-preview`,
      { params: date ? { date } : undefined },
    )
    return res.data.data
  },

  createGrowthAlertShare: async (
    learnerId: number,
    date: string,
  ): Promise<GrowthAlertShareResponse> => {
    const res = await apiClient.post<ApiResponse<GrowthAlertShareResponse>>(
      `/lc/admin/coaching/learners/${learnerId}/growth-alert-share`,
      undefined,
      { params: { date } },
    )
    return res.data.data
  },

  getPublicGrowthAlert: async (token: string): Promise<PublicGrowthAlertResponse> => {
    const res = await apiClient.get<ApiResponse<PublicGrowthAlertResponse>>(
      `/lc/growth-alerts/${encodeURIComponent(token)}`,
    )
    return res.data.data
  },

  // ── Phase 5b — 챕터/누적 테스트 진행·채점 ──
  // 응답의 quizId 는 전부 String(LcTestItemDetailView.quizId + QuizInfoView.quizId), testId/scopeContentId 는 일반 PK → 일반 JSON 안전.
  listTests: async (learnerId: number): Promise<LcTestListView> => {
    const res = await apiClient.get<ApiResponse<LcTestListView>>(
      `/lc/admin/coaching/learners/${learnerId}/tests`,
    )
    return res.data.data
  },
  getTestDetail: async (learnerId: number, testId: number): Promise<LcTestDetailView> => {
    const res = await apiClient.get<ApiResponse<LcTestDetailView>>(
      `/lc/admin/coaching/learners/${learnerId}/tests/${testId}`,
    )
    return res.data.data
  },
  // 출제(멱등) — 동일 스코프 재요청 시 기존 테스트 반환. testId 만 사용.
  generateTest: async (
    learnerId: number,
    kind: string,
    chapterOrdinal: number,
    scopeContentId: number,
    force = false,
  ): Promise<{ testId: number }> => {
    const res = await apiClient.post<ApiResponse<{ testId: number }>>(
      `/lc/admin/coaching/learners/${learnerId}/tests`,
      { kind, chapterOrdinal, scopeContentId, force },
    )
    return res.data.data
  },
  gradeTestItem: async (
    learnerId: number,
    testId: number,
    quizId: string,
    isCorrect?: boolean,
    answer?: string,
    manualGrade = false,
  ): Promise<LcTestDetailView> => {
    const res = await apiClient.post<ApiResponse<LcTestDetailView>>(
      `/lc/admin/coaching/learners/${learnerId}/tests/${testId}/grade-item`,
      { quizId, isCorrect, answer, manualGrade },
    )
    return res.data.data
  },
  completeTest: async (learnerId: number, testId: number): Promise<LcTestDetailView> => {
    const res = await apiClient.post<ApiResponse<LcTestDetailView>>(
      `/lc/admin/coaching/learners/${learnerId}/tests/${testId}/complete`,
      {},
    )
    return res.data.data
  },
  retakeTest: async (
    learnerId: number,
    testId: number,
  ): Promise<{ testId: number; roundNo: number; status: string }> => {
    const res = await apiClient.post<ApiResponse<{ testId: number; roundNo: number; status: string }>>(
      `/lc/admin/coaching/learners/${learnerId}/tests/${testId}/retake`,
      {},
    )
    return res.data.data
  },

  // ── Phase 5c — 리포트 + 복습추출 + 출력 (전부 String-safe 일반 JSON) ──
  getTestReport: async (learnerId: number, testId: number): Promise<LcTestReportView> => {
    const res = await apiClient.get<ApiResponse<LcTestReportView>>(
      `/lc/admin/coaching/learners/${learnerId}/tests/${testId}/report`,
    )
    return res.data.data
  },
  getReviewCandidates: async (
    learnerId: number,
    testId: number,
    threshold: number,
  ): Promise<LcReviewCandidateView[]> => {
    const res = await apiClient.get<ApiResponse<LcReviewCandidateView[]>>(
      `/lc/admin/coaching/learners/${learnerId}/tests/${testId}/review-candidates`,
      { params: { threshold } },
    )
    return res.data.data
  },
  getTestPrintQuizIds: async (learnerId: number, testId: number): Promise<LcTestPrintView> => {
    const res = await apiClient.get<ApiResponse<LcTestPrintView>>(
      `/lc/admin/coaching/learners/${learnerId}/tests/${testId}/print-quizzes`,
    )
    return res.data.data
  },
}
